import { APP_VERSION, USAGE_EVENTS_ENDPOINT } from "AppConstants";
import { authHeader } from "app/services/auth.header.service";
import { isTrackedFeature, pageKeyForPath } from "./featureCatalog";

const SEND_AT = 20;
const DAY_MS = 24 * 60 * 60 * 1000;
const SESSION_STORAGE_KEY = "usageSessionId";
const SAFE_VERSION = /^[A-Za-z0-9._-]{1,20}$/;

/**
 * Collects page views and actions for usage analytics (HRMS-322) and hands them to `send` in
 * batches. Built as a factory so a test can drive its clock and collect what it sends.
 *
 * @since NEXT_VERSION
 */
export const createUsageTracker = ({ send, now = Date.now, sessionId, appVersion }) => {
  let queue = [];
  let openPage = null;

  const flush = () => {
    if (queue.length === 0) return;
    const events = queue;
    queue = [];
    send({ sessionId, appVersion, sentAt: now(), events });
  };

  const record = (type, feature, details) => {
    if (!isTrackedFeature(feature)) return;
    queue.push({ type, feature, ...details, occurredAt: now() });
    if (queue.length >= SEND_AT) flush();
  };

  const pageClosed = () => {
    if (!openPage) return;
    // Capped because the server refuses longer, and a tab left open for days says nothing anyway.
    const durationMs = Math.min(now() - openPage.openedAt, DAY_MS);
    record("PAGE_LEAVE", openPage.key, { page: openPage.key, durationMs });
    openPage = null;
  };

  const pageOpened = (pathname) => {
    pageClosed();
    const key = pageKeyForPath(pathname);
    if (!key) return;
    openPage = { key, openedAt: now() };
    record("PAGE_VIEW", key, { page: key });
  };

  const track = (feature) => record("FEATURE_USED", feature, openPage ? { page: openPage.key } : {});

  return { pageOpened, pageClosed, track, flush };
};

/**
 * Posts a batch as the signed-in user. keepalive lets it finish while the tab closes, and a failure
 * is dropped: losing some usage must never disturb the page.
 *
 * @since NEXT_VERSION
 */
export const sendUsageBatch = (batch) => {
  const headers = authHeader();
  if (!headers.Authorization) return;
  fetch(USAGE_EVENTS_ENDPOINT, { method: "POST", headers, body: JSON.stringify(batch), keepalive: true })
    .catch(() => {});
};

// One id per browser tab, kept across reloads, so a visit's pages read back in order as a journey.
const tabSessionId = () => {
  const fresh = () =>
    window.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const id = fresh();
    sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    return id;
  } catch {
    return fresh();
  }
};

const tracker = createUsageTracker({
  send: sendUsageBatch,
  sessionId: tabSessionId(),
  appVersion: SAFE_VERSION.test(APP_VERSION) ? APP_VERSION : undefined,
});

// @since NEXT_VERSION
export const { pageOpened, pageClosed, track, flush } = tracker;
